import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Banknote, Landmark, CalendarClock, CalendarCheck } from "lucide-react";
import { api, fmtCHF, formatApiErrorDetail } from "@/lib/api";
import DateField, { localToday, fmtCH } from "@/components/invoices/DateField";

const Btn = ({ onClick, Icon, children, testId, active }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`min-h-11 px-3 rounded-xl border text-sm font-semibold flex items-center gap-2 text-left transition-colors ${active ? "border-primary-c bg-primary-c text-white" : "border-c bg-white hover:border-primary-c/50"}`}>
    <Icon className="w-4 h-4 shrink-0" />{children}
  </button>
);

export default function VerifyItem({ inv, onDone }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(null);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const tid = `verify-${inv.id}`;
  const today = localToday();
  const badge = inv.due_date < today ? t("verify.late") : inv.due_date === today ? t("verify.today") : t("verify.soon");

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setMode(null); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const open = (m, d = "") => { setMode(mode === m ? null : m); setDate(d); setReason(""); };
  const confirm = () => {
    if (!date) { toast.error(t("verify.pick_date")); return; }
    if (mode === "transfer") return run(() => api.post(`/invoices/${inv.id}/pay`, { paid_date: date, payment_method: "virement" }), t("verify.done_paid"));
    if (mode === "postpone") {
      if (reason.trim().length < 3) { toast.error(t("verify.reason_short")); return; }
      return run(() => api.post(`/invoices/${inv.id}/postpone`, { due_date: date, reason: reason.trim() }), t("verify.done_postponed"));
    }
    return run(() => api.post(`/invoices/${inv.id}/confirm-date`, { invoice_date: date }), t("verify.done_date"));
  };

  return (
    <li className="rounded-2xl border border-c bg-white p-3 space-y-2" data-testid={tid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold truncate">{inv.supplier}</p>
          <p className="text-xs text-text-secondary">{t("verify.due_on", { date: fmtCH(inv.due_date) })}{inv.check_payment ? ` · ${badge}` : ""}</p>
        </div>
        <p className="font-display text-lg whitespace-nowrap">{fmtCHF(inv.amount)}</p>
      </div>
      {inv.check_payment && (
        <div className="grid gap-2 sm:grid-cols-3">
          <Btn Icon={Banknote} testId={`${tid}-cash`} onClick={() => run(() => api.post(`/invoices/${inv.id}/pay`, { paid_date: today, payment_method: "especes" }), t("verify.done_paid"))}>{t("verify.cash")}</Btn>
          <Btn Icon={Landmark} testId={`${tid}-transfer`} active={mode === "transfer"} onClick={() => open("transfer", today)}>{t("verify.transfer")}</Btn>
          <Btn Icon={CalendarClock} testId={`${tid}-postpone`} active={mode === "postpone"} onClick={() => open("postpone")}>{t("verify.postpone")}</Btn>
        </div>
      )}
      {inv.check_date && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`${tid}-date-check`}>
          <span className="text-[#B45309] font-semibold">{t("verify.date_check", { date: fmtCH(inv.invoice_date) })}</span>
          <Btn Icon={CalendarCheck} testId={`${tid}-date-ok`} onClick={() => run(() => api.post(`/invoices/${inv.id}/confirm-date`, { invoice_date: inv.invoice_date }), t("verify.done_date"))}>{t("verify.date_ok")}</Btn>
          <Btn Icon={CalendarClock} testId={`${tid}-date-fix`} active={mode === "date"} onClick={() => open("date", inv.invoice_date)}>{t("verify.date_fix")}</Btn>
        </div>
      )}
      {mode && (
        <div className="p-3 bg-surface-muted rounded-xl space-y-2" data-testid={`${tid}-panel`}>
          <span className="text-xs text-text-secondary block">{mode === "transfer" ? t("verify.transfer_date") : mode === "postpone" ? t("verify.new_due") : t("verify.date_fix")}</span>
          <DateField value={date} onChange={setDate} maxToday={mode !== "postpone"} minTomorrow={mode === "postpone"} autoOpen={mode === "postpone"} testId={`${tid}-date`} />
          {mode === "postpone" && (
            <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder={t("verify.reason_ph")} aria-label={t("verify.reason")} className="input-c text-sm" data-testid={`${tid}-reason`} />
          )}
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={confirm} className="btn-primary flex-1 !py-2 text-sm" data-testid={`${tid}-confirm`}>{t("verify.confirm")}</button>
            <button type="button" onClick={() => setMode(null)} className="btn-secondary !py-2 text-sm" data-testid={`${tid}-cancel`}>{t("verify.cancel")}</button>
          </div>
        </div>
      )}
    </li>
  );
}
